<template>
  <v-card class="mx-auto" :loading="loading" :disabled="loading">
    <v-card-title>
      <v-row>
        <v-col cols="12" md="8">
          <BtnCircle
            tLabel="Atrás"
            tPosition="bottom"
            icon="mdi-arrow-left"
            color="grey darken-3"
            size="x-small"
            :to="{
              name: 'providers.view',
              params: { id: provider_id },
            }"
          />
          <span class="text-h6 ml-1">
            {{ loading_provider ? "" : `${provider.name} | `
            }}{{ $route.meta.title }} ({{ total_rows }})
          </span>
        </v-col>

        <v-col cols="12" md="4" class="text-right">
          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <v-btn
                v-on="on"
                fab
                x-small
                class="ml-1"
                color="info"
                :loading="faqs_ldg"
                :disabled="faqs.length === 0"
                @click.stop="faqs_dlg = true"
              >
                <v-icon>mdi-help</v-icon>
              </v-btn>
            </template>
            <span>Ayuda</span>
          </v-tooltip>

          <BtnCircle
            v-if="canCreate"
            tLabel="Migración"
            tPosition="bottom"
            icon="mdi-file-table"
            color="warning"
            size="x-small"
            :to="{
              name: 'prestaciones.migracion',
              params: { provider_id: provider_id },
            }"
          />

          <v-tooltip bottom v-if="canCreate">
            <template v-slot:activator="{ on }">
              <v-btn
                v-on="on"
                fab
                x-small
                class="ml-1"
                color="success"
                @click="openCreate"
              >
                <v-icon>mdi-plus</v-icon>
              </v-btn>
            </template>
            <span>Crear prestación</span>
          </v-tooltip>
        </v-col>

        <v-col cols="12">
          <v-text-field
            v-model="search"
            append-icon="mdi-magnify"
            label="Buscar..."
            single-line
            hide-details
          />
        </v-col>
      </v-row>
    </v-card-title>

    <v-card-text>
      <v-data-table
        :headers="headers"
        :items="data"
        :search="search"
        :loading="loading"
        :options.sync="table_options"
        :footer-props="{
          'items-per-page-options': [50, 100, 200, -1],
        }"
      >
        <template v-slot:item.description="{ item }">
          <span v-text="item.description || '---'" />
        </template>

        <template v-slot:item.amount="{ item }">
          <span
            v-text="item.amount === null ? '---' : numberFormat(item.amount)"
          />
        </template>

        <template v-slot:item.action="{ item }">
          <v-tooltip left v-if="canUpdate">
            <template v-slot:activator="{ on }">
              <v-btn
                v-on="on"
                fab
                x-small
                color="primary"
                @click="openEdit(item.id)"
              >
                <v-icon>mdi-pencil</v-icon>
              </v-btn>
            </template>
            <span>Editar</span>
          </v-tooltip>
        </template>
      </v-data-table>
    </v-card-text>

    <FaqDlg v-model="faqs_dlg" :faqs="faqs" />

    <v-dialog v-model="dialog" max-width="1400" scrollable persistent>
      <v-card :loading="form_loading" :disabled="form_loading">
        <v-card-title class="d-flex align-center">
          <span class="text-h6">
            {{ is_editing ? "Editar prestación" : "Crear prestación" }}
          </span>
          <v-spacer />
          <v-btn icon :disabled="form_loading" @click="closeDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-divider />

        <v-card-text class="pt-4">
          <v-form ref="form_data" lazy-validation>
            <v-row>
              <v-col cols="12">
                <v-card outlined>
                  <v-card-title>
                    <h2 class="text-caption">GENERAL</h2>
                  </v-card-title>
                  <v-divider />
                  <v-card-text>
                    <v-row>
                      <v-col cols="12" md="6">
                        <v-autocomplete
                          v-model="form.benefit_type_id"
                          label="Tipo*"
                          :rules="rules.required"
                          :items="benefit_types"
                          item-value="id"
                          :item-text="(item) => item.benefit_type"
                          :loading="benefit_types_loading"
                        />
                      </v-col>
                      <v-col cols="12" md="6">
                        <v-text-field
                          v-model="form.code"
                          label="Código*"
                          :rules="rules.required"
                          maxlength="30"
                        />
                      </v-col>
                    </v-row>
                  </v-card-text>
                </v-card>
              </v-col>

              <v-col cols="12">
                <v-card outlined>
                  <v-card-title>
                    <h2 class="text-caption">DESCRIPCIONES</h2>
                  </v-card-title>
                  <v-divider />
                  <v-card-text>
                    <v-row dense>
                      <v-col
                        v-for="(benefit_detail, i) in form.benefit_details"
                        :key="i"
                        cols="12"
                      >
                        <v-row v-if="benefit_detail.active">
                          <v-col cols="12" md="3">
                            <v-text-field
                              v-model="benefit_detail.description"
                              :label="`Descripción* (${i + 1})`"
                              :rules="rules.text250Required"
                            />
                          </v-col>
                          <v-col cols="12" md="2">
                            <v-text-field
                              v-model="benefit_detail.amount"
                              label="Precio*"
                              type="number"
                              :rules="rules.required"
                            />
                          </v-col>
                          <v-col cols="12" md="2">
                            <v-text-field
                              v-model="benefit_detail.frequency"
                              label="Frecuencia"
                              type="number"
                            />
                          </v-col>
                          <v-col cols="12" md="2">
                            <v-autocomplete
                              v-model="benefit_detail.benefit_type_id"
                              label="Tipo*"
                              :rules="rules.required"
                              :items="benefit_types"
                              item-value="id"
                              :item-text="(item) => item.benefit_type"
                              :loading="benefit_types_loading"
                            />
                          </v-col>
                          <v-col cols="12" md="2">
                            <v-text-field
                              v-model="benefit_detail.bill"
                              label="Factura única"
                              maxlength="30"
                            />
                          </v-col>
                          <v-col
                            cols="12"
                            md="1"
                            align-self="center"
                            class="text-center"
                          >
                            <v-tooltip bottom>
                              <template v-slot:activator="{ on }">
                                <v-btn
                                  v-on="on"
                                  icon
                                  x-small
                                  color="error"
                                  @click="deleteBenefitDetail(i)"
                                >
                                  <v-icon>mdi-close</v-icon>
                                </v-btn>
                              </template>
                              <span>Eliminar descripción</span>
                            </v-tooltip>
                          </v-col>
                          <v-col cols="12">
                            <v-textarea
                              v-model="benefit_detail.definition"
                              label="Definición"
                              rows="1"
                            />
                            <v-divider />
                          </v-col>
                        </v-row>
                      </v-col>
                      <v-col cols="12">
                        <v-btn
                          block
                          x-small
                          dark
                          color="warning"
                          @click="addBenefitDetail"
                        >
                          <v-icon left>mdi-plus</v-icon>
                          Agregar descripción
                        </v-btn>
                      </v-col>
                    </v-row>
                  </v-card-text>
                </v-card>
              </v-col>

              <v-col cols="12">
                <v-card outlined>
                  <v-card-title>
                    <h2 class="text-caption">PRESTACIONES INCLUIDAS</h2>
                  </v-card-title>
                  <v-divider />
                  <v-card-text>
                    <v-row dense>
                      <v-col
                        v-for="(benefit_included, i) in form.benefit_includes"
                        :key="i"
                        cols="12"
                      >
                        <v-row v-if="benefit_included.active">
                          <v-col cols="12" md="11">
                            <v-autocomplete
                              v-model="benefit_included.benefit_included_id"
                              :label="`Prestación* (${i + 1})`"
                              :rules="rules.required"
                              :items="available_benefits"
                              item-value="id"
                              :item-text="benefitOptionText"
                            />
                          </v-col>
                          <v-col
                            cols="12"
                            md="1"
                            align-self="center"
                            class="text-center"
                          >
                            <v-tooltip bottom>
                              <template v-slot:activator="{ on }">
                                <v-btn
                                  v-on="on"
                                  icon
                                  x-small
                                  color="error"
                                  @click="deleteBenefitInclude(i)"
                                >
                                  <v-icon>mdi-close</v-icon>
                                </v-btn>
                              </template>
                              <span>Eliminar prestación incluida</span>
                            </v-tooltip>
                          </v-col>
                        </v-row>
                      </v-col>
                      <v-col cols="12">
                        <v-btn
                          block
                          x-small
                          dark
                          color="warning"
                          @click="addBenefitInclude"
                        >
                          <v-icon left>mdi-plus</v-icon>
                          Agregar prestación incluida
                        </v-btn>
                      </v-col>
                    </v-row>
                  </v-card-text>
                </v-card>
              </v-col>

              <v-col cols="12">
                <v-card outlined>
                  <v-card-title>
                    <h2 class="text-caption">COBERTURA POR ASEGURADORA</h2>
                  </v-card-title>
                  <v-divider />
                  <v-card-text>
                    <v-row dense>
                      <v-col
                        v-for="(
                          coverage, i
                        ) in form.benefit_insurance_coverages"
                        :key="i"
                        cols="12"
                      >
                        <v-row v-if="coverage.active">
                          <v-col cols="12" md="3">
                            <v-autocomplete
                              v-model="coverage.insurance_id"
                              :label="`Aseguradora* (${i + 1})`"
                              :rules="rules.required"
                              :items="insurances"
                              item-value="id"
                              :item-text="(item) => item.name"
                              :loading="insurances_loading"
                            />
                          </v-col>
                          <v-col cols="12" md="4">
                            <v-autocomplete
                              v-model="coverage.coverage_type_id"
                              :label="`Cobertura (${i + 1})`"
                              :items="coverage_types"
                              item-value="id"
                              :item-text="(item) => item.coverage_type"
                              :loading="coverage_types_loading"
                            />
                          </v-col>
                          <v-col cols="12" md="4">
                            <v-autocomplete
                              v-model="coverage.contractor_id"
                              label="Contratante"
                              :items="contractors"
                              item-value="id"
                              :item-text="(item) => item.name"
                              :loading="contractors_loading"
                            />
                          </v-col>
                          <v-col
                            cols="12"
                            md="1"
                            align-self="center"
                            class="text-center"
                          >
                            <v-tooltip bottom>
                              <template v-slot:activator="{ on }">
                                <v-btn
                                  v-on="on"
                                  icon
                                  x-small
                                  color="error"
                                  @click="deleteCoverage(i)"
                                >
                                  <v-icon>mdi-close</v-icon>
                                </v-btn>
                              </template>
                              <span>Eliminar cobertura</span>
                            </v-tooltip>
                          </v-col>
                        </v-row>
                      </v-col>
                      <v-col cols="12">
                        <v-btn
                          block
                          x-small
                          dark
                          color="warning"
                          @click="addCoverage"
                        >
                          <v-icon left>mdi-plus</v-icon>
                          Agregar cobertura
                        </v-btn>
                      </v-col>
                    </v-row>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>

        <v-divider />

        <v-card-actions class="pa-4">
          <v-btn
            v-if="is_editing && canDelete"
            color="error"
            text
            :disabled="form_loading"
            @click="handleDelete"
          >
            <v-icon left>mdi-delete</v-icon>
            Eliminar
          </v-btn>
          <v-spacer />
          <v-btn text :disabled="form_loading" @click="closeDialog">
            Cancelar
          </v-btn>
          <v-btn
            :color="is_editing ? 'info' : 'success'"
            :loading="form_loading"
            :disabled="!form_ready"
            @click="handleSubmit"
          >
            <v-icon left>{{
              is_editing ? "mdi-content-save" : "mdi-plus"
            }}</v-icon>
            {{ is_editing ? "Guardar cambios" : "Crear" }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import Axios from "axios";
import {
  URL_API,
  headersToken,
  msgConfirm,
  msgAlert,
  rules,
} from "../../control";
import { catalog } from "../../requests/catalogRequest";
import { index, show, storeUpdate, destroy } from "../../requests/pageRequest";
import BtnCircle from "../../components/BtnCircle.vue";
import FaqDlg from "../../components/FaqDlg.vue";

export default {
  components: {
    BtnCircle,
    FaqDlg,
  },

  data() {
    return {
      provider_id: this.$route.params.provider_id,
      login: this.$store.getters.getLogin,
      loading: true,
      loading_provider: true,
      provider: {},
      data: [],
      search: "",
      table_options: {
        page: 1,
        itemsPerPage: 10,
        sortBy: [],
        sortDesc: [],
      },
      headers: [
        { text: "Tipo", value: "benefit_type_name" },
        { text: "Código", value: "code" },
        { text: "Descripción", value: "description" },
        { text: "Coberturas", value: "coverages" },
        { text: "Cant. desc.", value: "description_total" },
        { text: "Monto", value: "amount" },
        {
          text: "Acciones",
          value: "action",
          sortable: false,
          filterable: false,
        },
      ],
      dialog: false,
      form_loading: false,
      form: {
        id: null,
        code: "",
        benefit_type_id: null,
        provider_id: this.$route.params.provider_id,
        created_by_id: this.$store.getters.getLogin.id,
        updated_by_id: this.$store.getters.getLogin.id,
        benefit_details: [],
        benefit_includes: [],
        benefit_insurance_coverages: [],
      },
      rules: rules(),
      benefit_types: [],
      benefit_types_loading: true,
      insurances: [],
      insurances_loading: true,
      coverage_types: [],
      coverage_types_loading: true,
      contractors: [],
      contractors_loading: true,
      faqs: [],
      faqs_dlg: false,
      faqs_ldg: true,
      table_options: {
        page: 1,
        itemsPerPage: 50,
      },
    };
  },

  computed: {
    total_rows() {
      return this.data.length;
    },
    benefit_permissions() {
      return this.login && this.login.permissions
        ? this.login.permissions.benefits || {}
        : {};
    },
    canCreate() {
      return Boolean(this.benefit_permissions.create);
    },
    canUpdate() {
      return Boolean(this.benefit_permissions.update);
    },
    canDelete() {
      return Boolean(this.benefit_permissions.delete);
    },
    is_editing() {
      return this.form.id !== null && this.form.id !== undefined;
    },
    form_ready() {
      return (
        !this.form_loading &&
        !this.benefit_types_loading &&
        !this.insurances_loading &&
        !this.coverage_types_loading &&
        !this.contractors_loading
      );
    },
    available_benefits() {
      return this.data.filter(
        (item) => Number(item.id) !== Number(this.form.id),
      );
    },
  },

  methods: {
    newForm() {
      return {
        id: null,
        code: "",
        benefit_type_id: null,
        provider_id: this.provider_id,
        created_by_id: this.login.id,
        updated_by_id: this.login.id,
        benefit_details: [],
        benefit_includes: [],
        benefit_insurance_coverages: [],
      };
    },

    normalizeBenefit(benefit) {
      const details = Array.isArray(benefit.benefit_details)
        ? benefit.benefit_details
        : [];
      const first_detail = details.length > 0 ? details[0] : null;
      const coverages = Array.isArray(benefit.benefit_insurance_coverages)
        ? benefit.benefit_insurance_coverages
        : [];

      return {
        ...benefit,
        benefit_details: details,
        benefit_type_name: benefit.benefit_type
          ? benefit.benefit_type.benefit_type
          : "",
        description: first_detail ? first_detail.description : "",
        description_total: details.length,
        amount:
          first_detail && first_detail.amount !== null
            ? Number(first_detail.amount)
            : null,
        coverages:
          typeof benefit.coverages === "string"
            ? benefit.coverages
            : coverages
                .filter((item) => item.coverage_type && item.insurance)
                .map(
                  (item) =>
                    `${item.coverage_type.coverage_type} (${item.insurance.name})`,
                )
                .join(" | "),
      };
    },

    benefitOptionText(item) {
      const type = item.benefit_type_name || "";
      const code = item.code || "";
      const description = item.description || "";
      return `${type} | ${code} | ${description}`;
    },

    numberFormat(value) {
      const amount = Number(value);
      return Number(
        (Number.isFinite(amount) ? amount : 0).toFixed(2),
      ).toLocaleString("en-US", {
        style: "currency",
        currency: "USD",
      });
    },

    errorMessage(error) {
      if (
        error &&
        error.response &&
        error.response.data &&
        error.response.data.message
      ) {
        return error.response.data.message;
      }
      return error && error.message
        ? error.message
        : "No fue posible completar la operación.";
    },

    async loadBenefits() {
      this.loading = true;
      try {
        const response = await index(
          "benefits/provider/" + this.provider_id,
          this.login.token,
          [],
        );
        if (
          !response ||
          response.success === false ||
          !Array.isArray(response.data)
        ) {
          throw new Error(
            response && response.message
              ? response.message
              : "No se pudo cargar el listado.",
          );
        }
        this.data = response.data.map((item) => this.normalizeBenefit(item));
      } catch (error) {
        this.$swal.fire(msgAlert("error", this.errorMessage(error)));
      } finally {
        this.loading = false;
      }
    },

    async loadProvider() {
      try {
        this.provider = await show(
          "providers",
          this.login.token,
          this.provider_id,
        );
      } catch (error) {
        this.$swal.fire(msgAlert("error", this.errorMessage(error)));
      } finally {
        this.loading_provider = false;
      }
    },

    async loadFaqs() {
      try {
        const response = await Axios.get(
          URL_API + "/faqs/faq_interfaces/" + this.$route.meta.title,
          headersToken(this.login.token),
        );
        this.faqs =
          response.data && Array.isArray(response.data.data)
            ? response.data.data
            : [];
      } catch (error) {
        this.faqs = [];
      } finally {
        this.faqs_ldg = false;
      }
    },

    async loadCatalogs() {
      const requests = [
        catalog("benefit_types", this.login.token)
          .then((response) => {
            this.benefit_types = response;
          })
          .finally(() => {
            this.benefit_types_loading = false;
          }),
        index("insurances", this.login.token, [
          { name: "active", value: 1 },
          { name: "id", value: this.login.id },
        ])
          .then((response) => {
            this.insurances = response.data;
          })
          .finally(() => {
            this.insurances_loading = false;
          }),
        index("coverage/types", this.login.token, [])
          .then((response) => {
            this.coverage_types = response.data;
          })
          .finally(() => {
            this.coverage_types_loading = false;
          }),
        index("contractors", this.login.token, [])
          .then((response) => {
            this.contractors = response.data.concat([
              { id: null, name: "TODOS", active: 1 },
            ]);
          })
          .finally(() => {
            this.contractors_loading = false;
          }),
      ];

      const results = await Promise.all(
        requests.map((promise) => promise.then(() => true).catch(() => false)),
      );

      if (results.some((result) => !result)) {
        this.$swal.fire(
          msgAlert(
            "error",
            "No fue posible cargar uno o más catálogos del formulario.",
          ),
        );
      }
    },

    openCreate() {
      if (!this.canCreate) return;
      this.form = this.newForm();
      this.dialog = true;
      this.$nextTick(() => {
        if (this.$refs.form_data) this.$refs.form_data.resetValidation();
      });
    },

    async openEdit(id) {
      if (!this.canUpdate) return;
      this.form = this.newForm();
      this.dialog = true;
      this.form_loading = true;
      try {
        const benefit = await show("benefits", this.login.token, id);
        if (!benefit || !benefit.id) {
          throw new Error("No se encontró la prestación seleccionada.");
        }
        this.form = {
          ...this.newForm(),
          ...benefit,
          benefit_details: benefit.benefit_details || [],
          benefit_includes: benefit.benefit_includes || [],
          benefit_insurance_coverages:
            benefit.benefit_insurance_coverages || [],
          provider_id: this.provider_id,
          updated_by_id: this.login.id,
        };
        this.$nextTick(() => {
          if (this.$refs.form_data) this.$refs.form_data.resetValidation();
        });
      } catch (error) {
        this.dialog = false;
        this.$swal.fire(msgAlert("error", this.errorMessage(error)));
      } finally {
        this.form_loading = false;
      }
    },

    closeDialog() {
      if (this.form_loading) return;
      this.dialog = false;
      this.form = this.newForm();
    },

    async handleSubmit() {
      if (this.form_loading || !this.form_ready) return;

      if (!this.$refs.form_data.validate()) {
        this.$swal.fire(
          msgAlert(
            "error",
            "Formulario inválido, revisa los campos señalados.",
          ),
        );
        return;
      }

      if (!this.form.benefit_details.some((item) => Boolean(item.active))) {
        this.$swal.fire(
          msgAlert("error", "Debe existir al menos una descripción activa."),
        );
        return;
      }

      const is_editing = this.is_editing;
      const id = this.form.id;
      const confirmation = await this.$swal.fire(
        msgConfirm(
          `¿Confirma la ${is_editing ? "edición" : "creación"} del registro?`,
        ),
      );
      if (!confirmation.isConfirmed) return;

      this.form_loading = true;
      let saved = false;
      try {
        const response = await storeUpdate(
          "benefits",
          this.login.token,
          is_editing ? id : 0,
          this.form,
          this.login.id,
        );

        if (!response || !response.success) {
          throw new Error(
            response && response.message
              ? response.message
              : "No se pudo guardar la prestación.",
          );
        }
        saved = true;
        const benefit_id = is_editing ? id : response.data && response.data.id;

        if (!benefit_id) {
          throw new Error(
            "El servidor guardó el registro, pero no devolvió su identificador.",
          );
        }

        // Si la consulta posterior falla, un segundo guardado actualizará
        // el registro creado en lugar de generar otra prestación.
        if (!is_editing) this.form.id = benefit_id;

        const benefit = await show("benefits", this.login.token, benefit_id);
        if (!benefit || !benefit.id) {
          throw new Error(
            "El registro fue guardado, pero no se pudo recuperar su información.",
          );
        }

        const row = this.normalizeBenefit(benefit);
        if (is_editing) {
          const index = this.data.findIndex(
            (item) => Number(item.id) === Number(benefit_id),
          );
          if (index === -1) {
            throw new Error(
              "El registro se guardó, pero no se encontró su fila en el listado.",
            );
          }
          this.$set(this.data, index, row);
        } else {
          this.data.push(row);
        }

        this.form_loading = false;
        this.closeDialog();
        this.$swal.fire(msgAlert("success", response.message));
      } catch (error) {
        const prefix = saved
          ? "Los cambios se guardaron en CLUB, pero no se pudo sincronizar la fila. "
          : "";
        this.$swal.fire(msgAlert("error", prefix + this.errorMessage(error)));
      } finally {
        this.form_loading = false;
      }
    },

    async handleDelete() {
      if (!this.is_editing || !this.canDelete || this.form_loading) return;
      const id = this.form.id;
      const confirmation = await this.$swal.fire(
        msgConfirm("¿Confirma la desactivación de la prestación?"),
      );
      if (!confirmation.isConfirmed) return;

      this.form_loading = true;
      try {
        const response = await destroy(
          "benefits",
          this.login.token,
          id,
          this.login.id,
        );
        if (!response || !response.success) {
          throw new Error(
            response && response.message
              ? response.message
              : "No se pudo eliminar la prestación.",
          );
        }
        const index = this.data.findIndex(
          (item) => Number(item.id) === Number(id),
        );
        if (index !== -1) this.data.splice(index, 1);
        this.form_loading = false;
        this.closeDialog();
        this.$swal.fire(msgAlert("success", response.message));
      } catch (error) {
        this.$swal.fire(msgAlert("error", this.errorMessage(error)));
      } finally {
        this.form_loading = false;
      }
    },

    addBenefitDetail() {
      this.form.benefit_details.push({
        id: null,
        description: "",
        amount: "",
        definition: "",
        frequency: "0",
        benefit_type_id: this.form.benefit_type_id,
        bill: "",
        active: 1,
      });
    },

    async deleteBenefitDetail(i) {
      const confirmation = await this.$swal.fire(
        msgConfirm(`¿Confirma eliminar la descripción (${i + 1})?`),
      );
      if (!confirmation.isConfirmed) return;
      const item = this.form.benefit_details[i];
      if (!item) return;
      if (item.id === null || item.id === undefined) {
        this.form.benefit_details.splice(i, 1);
      } else {
        item.active = false;
      }
    },

    addBenefitInclude() {
      this.form.benefit_includes.push({
        id: null,
        benefit_included_id: null,
        active: 1,
      });
    },

    async deleteBenefitInclude(i) {
      const confirmation = await this.$swal.fire(
        msgConfirm(`¿Confirma eliminar la prestación incluida (${i + 1})?`),
      );
      if (!confirmation.isConfirmed) return;
      const item = this.form.benefit_includes[i];
      if (!item) return;
      if (item.id === null || item.id === undefined) {
        this.form.benefit_includes.splice(i, 1);
      } else {
        item.active = false;
      }
    },

    addCoverage() {
      this.form.benefit_insurance_coverages.push({
        id: null,
        insurance_id: null,
        contractor_id: null,
        coverage_type_id: null,
        active: 1,
      });
    },

    async deleteCoverage(i) {
      const confirmation = await this.$swal.fire(
        msgConfirm(`¿Confirma eliminar la cobertura (${i + 1})?`),
      );
      if (!confirmation.isConfirmed) return;
      const item = this.form.benefit_insurance_coverages[i];
      if (!item) return;
      if (item.id === null || item.id === undefined) {
        this.form.benefit_insurance_coverages.splice(i, 1);
      } else {
        item.active = false;
      }
    },
  },

  mounted() {
    this.loadFaqs();
    this.loadProvider();
    this.loadBenefits();
    this.loadCatalogs();
  },
};
</script>
